import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, ShoppingBag, Briefcase, MessageCircle, Receipt, LogOut, Menu, X, ShieldCheck, Gift } from "lucide-react";
import { useState } from "react";
import LogoMark from "../LogoMark";

const nav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/lots", label: "Browse", icon: ShoppingBag, testid: "nav-lots" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, testid: "nav-portfolio" },
  { to: "/referrals", label: "Refer", icon: Gift, testid: "nav-referrals" },
  { to: "/chat", label: "EarnBot", icon: MessageCircle, testid: "nav-chat" },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const kycBadge = (status) => {
    const map = {
      not_submitted: ["bg-amber-100 text-amber-800", "Submit KYC"],
      pending: ["bg-blue-100 text-blue-800", "KYC Pending"],
      approved: ["bg-[#FCEDED] text-[#D71921]", "KYC Approved"],
      rejected: ["bg-red-100 text-red-800", "KYC Rejected"],
    };
    const [cls, label] = map[status] || map.not_submitted;
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid="kyc-status-badge">{label}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F8F4E9] pb-20 md:pb-0">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E8E0D2]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <LogoMark size="md" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => {
              const active = location.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={n.testid}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active ? "bg-[#FCEDED] text-[#D71921]" : "text-[#6B5F58] hover:text-[#1F1816]"
                  }`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {kycBadge(user?.kyc_status)}
            <button onClick={handleLogout} data-testid="logout-btn" className="hidden md:flex items-center gap-2 px-3 py-2 text-sm text-[#6B5F58] hover:text-[#1F1816]">
              <LogOut className="w-4 h-4" /> Logout
            </button>
            <button onClick={() => setOpen(!open)} className="md:hidden text-[#1F1816]" data-testid="mobile-menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {open && (
          <div className="md:hidden border-t border-[#E8E0D2] bg-white">
            <div className="px-4 py-3 space-y-1">
              {nav.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-[#1F1816] hover:bg-[#F8F4E9]">
                  <n.icon className="w-5 h-5" /> {n.label}
                </Link>
              ))}
              <button onClick={handleLogout} className="flex w-full items-center gap-3 px-3 py-3 rounded-lg text-red-600">
                <LogOut className="w-5 h-5" /> Logout
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <Outlet />
      </main>

      {/* Mobile bottom tab */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E8E0D2] z-40 gf-bottom-safe">
        <div className="grid grid-cols-5">
          {nav.map((n) => {
            const active = location.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`m-${n.testid}`}
                className={`flex flex-col items-center justify-center py-2 text-xs ${
                  active ? "text-[#D71921]" : "text-[#6B5F58]"
                }`}>
                <n.icon className="w-5 h-5 mb-0.5" />
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
